import {
  Module,
  VuexModule,
  Action,
  Mutation,
  getModule
} from 'vuex-module-decorators'
import Vue from 'vue'
import axios from 'axios'

import store from '@/store'
import authModule from '@/store/auth'
import dashboardModule from '@/store/dashboard'
import listModule from '@/store/list'
import { Whitelabel } from '@/types/Whitelabel'
import { User } from '@/types/User'

const name = 'whitelabel'

@Module({
  namespaced: true,
  dynamic: true,
  name,
  store
})
class WhitelabelModule extends VuexModule {
  private currentWhitelabel!: Whitelabel
  private allWhitelabels: Whitelabel[] = []

  private currentAdmin?: User
  private allAdmins: any[] = []

  get whitelabel(): Whitelabel {
    return this.currentWhitelabel
  }

  get whitelabels(): Whitelabel[] {
    return this.allWhitelabels
  }

  get admin(): User | undefined {
    return this.currentAdmin
  }

  get admins(): any[] {
    return this.allAdmins
  }

  @Mutation
  setCurrentWhitelabel(payload: Whitelabel) {
    Vue.set(this, 'currentWhitelabel', payload)
  }

  @Mutation
  setAllWhitelabels(payload: Whitelabel[]) {
    Vue.set(this, 'allWhitelabels', payload)
  }

  @Mutation
  setCurrentAdmin(payload: User) {
    Vue.set(this, 'currentAdmin', payload)
  }

  @Mutation
  setAllAdmins(payload: User[]) {
    Vue.set(this, 'allAdmins', payload)
  }

  @Action({ rawError: true })
  async fetch(id: string) {
    try {
      const { data } = await axios.get(`/admin/findoo/whitelabel/${id}`)
      this.context.commit('setCurrentWhitelabel', data)
    } catch (error) {
      dashboardModule.newError('Whitelabel konnte nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async fetchAll(params?: {
    limit: number
    q?: string
    page?: number
    sortBy?: string
    sortDesc?: boolean
  }) {
    try {
      const { data } = await axios.get('/admin/findoo/whitelabel/list', {
        params
      })
      this.context.commit('setAllWhitelabels', data.whitelabels)
      return data.page_count
    } catch (error) {
      dashboardModule.newError('Whitelabels konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async create(payload: Partial<Whitelabel>) {
    try {
      const { data } = await axios.post(`/admin/findoo/whitelabel`, payload)
      this.context.commit('setCurrentWhitelabel', data)
      this.fetchAll({ limit: listModule.perPage })
      dashboardModule.newMessage('Whitelabel wurde erfolgreich erstellt')
    } catch (error) {
      dashboardModule.newError('Whitelabel konnte nicht erstellt werden')
    }
  }

  @Action({ rawError: true })
  async update(payload: Partial<Whitelabel>) {
    try {
      const { data } = await axios.put(
        `/admin/findoo/whitelabel/${payload._id}`,
        payload
      )
      this.context.commit('setCurrentWhitelabel', data.whitelabel)
      dashboardModule.newMessage('Whitelabel wurde erfolgreich aktualisiert')
    } catch (error) {
      dashboardModule.newError('Whitelabel konnte nicht aktualisiert werden')
    }
  }

  @Action({ rawError: true })
  async fetchAllAdmins(params: {
    limit: number
    q?: string
    page?: number
    sortBy?: string
    sortDesc?: boolean
  }) {
    try {
      const { data } = await axios.get(
        `/admin/${this.whitelabel!.slug}/whitelabel/users`,
        {
          params
        }
      )
      this.context.commit('setAllAdmins', data.user)
      return data.page_count
    } catch (error) {
      dashboardModule.newError('Admins konnten nicht geladen werden')
    }
  }

  @Action({ rawError: true })
  async createAdmin(payload: { whitelabel: string; email: string }) {
    try {
      await axios.post(`/admin/${payload.whitelabel}/whitelabel/users`, payload)
      dashboardModule.newMessage('Admin wurde erfolgreich erstellt')
    } catch (error) {
      dashboardModule.newError('Admin konnte nicht erstellt werden')
    }
  }

  @Action({ rawError: true })
  async deleteAdmin(payload: { whitelabel: string; userId: string }) {
    try {
      await axios.delete(
        `/admin/${payload.whitelabel}/whitelabel/users/${payload.userId}`
      )
      dashboardModule.newMessage('Admin wurde erfolgreich gelöscht')
    } catch (error) {
      dashboardModule.newError('Admin konnte nicht gelöscht werden')
    }
  }

  @Action({ rawError: true })
  setWhitelabel(payload: Whitelabel) {
    if (authModule.isSuperadmin) {
      localStorage.setItem('currentWl', payload._id)
    }
    this.context.commit('setCurrentWhitelabel', payload)
  }

  @Action({ rawError: true })
  setAdmin(payload: User) {
    this.context.commit('setCurrentAdmin', payload)
  }

  @Action({ rawError: true })
  setAdmins(payload: User[]) {
    this.context.commit('setAllAdmins', payload)
  }
}

export default getModule(WhitelabelModule)
